<template>
  <div class="modal-mask">
    <div class="modal alarm-modal">
      <div class="modal-hdr">
        <button class="ico-btn" @click="$emit('close')" title="Close without saving">
            <!-- Back arrow -->
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <h3>Alarm Settings</h3>
        <button class="ico-btn" @click="save" title="Save and close">
            <!-- Check icon -->
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check"><path d="M20 6 9 17l-5-5"/></svg>
        </button>
      </div>

      <div class="modal-body scrollable">
        <p class="subtitle">Temperature and humidity alarm switches and ranges can be set.</p>

        <section class="section-block">
            <h4 class="sec-title">Alarm settings</h4>

            <!-- TEMPERATURE -->
            <div class="control-row">
                <span class="control-label">Temperature Upper and Lower Limits</span>
                <div 
                    class="toggle-switch" 
                    :class="{ active: tempEnabled }" 
                    @click="tempEnabled = !tempEnabled"
                >
                    <span class="knob"></span>
                </div>
            </div>
            
            <div class="slider-wrapper" v-if="tempEnabled">
                <DualRangeSlider 
                    :min="-20" 
                    :max="80" 
                    :step="0.1" 
                    v-model="tempRange"
                    unit="°C"
                />
            </div>

            <!-- HUMIDITY -->
            <div class="control-row mt-4">
                <span class="control-label">Humidity Upper and Lower Limits</span>
                <div 
                    class="toggle-switch" 
                    :class="{ active: humEnabled }" 
                    @click="humEnabled = !humEnabled"
                >
                    <span class="knob"></span>
                </div>
            </div>

            <div class="slider-wrapper" v-if="humEnabled">
                <DualRangeSlider 
                    :min="0" 
                    :max="100" 
                    :step="1" 
                    v-model="humRange"
                    unit="%"
                />
            </div>
        </section>

        <section class="section-block">
            <h4 class="sec-title">Alarm Push</h4>
            <div class="control-row with-arrow clickable" @click="showAlarmPush = !showAlarmPush">
                <span class="control-label bold">Alarm</span>
                <div class="row-right">
                    <span class="value-text">{{ getNotificationSummary }}</span>
                    <svg class="chevron" :class="{ rotated: showAlarmPush }" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </div>
            </div>
            
            <!-- Expandable notification preferences -->
            <transition name="expand">
                <div v-if="showAlarmPush" class="expanded-section">
                    <div class="notification-options">
                        <label class="notification-option">
                            <input type="checkbox" v-model="notifyEmail" />
                            <span class="option-label">Notifica Email</span>
                        </label>
                    </div>
                </div>
            </transition>
        </section>

        <section class="section-block">
            <h4 class="sec-title">Temp/Hum/Anti-tamper/Record</h4>
            <div class="control-row with-arrow clickable" @click="showAlarmLog = !showAlarmLog">
                <span class="control-label bold">Alarm_log</span>
                <div class="row-right">
                    <span class="value-text text-truncate">{{ alarmLogCount }} events</span>
                    <svg class="chevron" :class="{ rotated: showAlarmLog }" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </div>
            </div>
            
            <!-- Expandable alarm log -->
            <transition name="expand">
                <div v-if="showAlarmLog" class="expanded-section">
                    <div class="alarm-log-list">
                        <div v-if="alarmLog.length === 0" class="no-logs">
                            No alarm events recorded
                        </div>
                        <div v-for="(log, index) in alarmLog" :key="index" class="log-item">
                            <div class="log-type" :class="log.type">{{ log.type.toUpperCase() }}</div>
                            <div class="log-details">
                                <div class="log-message">{{ log.message }}</div>
                                <div class="log-time">{{ formatLogTime(log.timestamp) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </section>

      </div>
      
      <!-- Bottom bar (optional styling based on screenshots usually have saves in header, 
           but here we can keep a save button or rely on header 'check' icon) -->
      <!-- <div class="modal-actions">
        <button @click="save" :disabled="saving">
            {{ saving ? 'Saving...' : 'Save' }}
        </button>
      </div> -->
    </div>
  </div>
  
  <PermissionRequestModal
      v-if="showPermissionModal"
      :deviceId="deviceId"
      permissionType="change_thresholds"
      @close="onPermissionModalClose" 
  />
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue';
import axios from '@/axiosInstance';
import { useToast } from 'vue-toast-notification';
import PermissionRequestModal from '@/components/PermissionRequestModal.vue';
import DualRangeSlider from '@/components/ui/DualRangeSlider.vue';

const props = defineProps<{
  deviceId: number;
}>();

const emit = defineEmits(['close', 'saved']);
const toast = useToast();

const saving = ref(false);

// Local state
const tempEnabled = ref(false);
const humEnabled = ref(false);

const tempRange = ref<[number, number]>([10, 40]); // Default fallback
const humRange = ref<[number, number]>([30, 70]); // Default fallback

// Expandable sections state
const showAlarmPush = ref(false);
const showAlarmLog = ref(false);

// Notification preferences
const notifyEmail = ref(false);

// Permission Modal State
const showPermissionModal = ref(false);


// Alarm log (mock data for now)
const alarmLog = ref([
    { type: 'temp', message: 'Temperature exceeded 40°C', timestamp: new Date(Date.now() - 3600000) },
    { type: 'hum', message: 'Humidity below 30%', timestamp: new Date(Date.now() - 7200000) },
    { type: 'temp', message: 'Temperature recovered to normal', timestamp: new Date(Date.now() - 10800000) },
]);

// Computed properties
const getNotificationSummary = computed(() => {
    return notifyEmail.value ? 'Email' : 'None';
});

const alarmLogCount = computed(() => alarmLog.value.length);

function formatLogTime(timestamp: Date): string {
    return new Date(timestamp).toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

// Load current settings
onMounted(async () => {
  try {
    const { data } = await axios.get(`/devices/${props.deviceId}/get-graph`);
    if (data) {
        if (data.temp_min != null && data.temp_max != null) {
            tempEnabled.value = true;
            tempRange.value = [data.temp_min, data.temp_max];
        } else {
            tempEnabled.value = false;
        }

        if (data.hum_min != null && data.hum_max != null) {
            humEnabled.value = true;
            humRange.value = [data.hum_min, data.hum_max];
        } else {
            humEnabled.value = false;
        }

        notifyEmail.value = !!data.notify_email;
    }
  } catch (e) {
    console.error("Errore caricamento settings:", e);
    toast.error("Impossibile caricare le impostazioni attuali");
  }
});

async function save() {
  saving.value = true;
  try {
     const payload = {
        temp_min: tempEnabled.value ? tempRange.value[0] : null,
        temp_max: tempEnabled.value ? tempRange.value[1] : null,
        hum_min: humEnabled.value ? humRange.value[0] : null,
        hum_max: humEnabled.value ? humRange.value[1] : null,
        notify_email: notifyEmail.value,
     };

     await axios.post(`/devices/${props.deviceId}/thresholds`, payload);
     
     toast.success("Impostazioni salvate con successo", { duration: 2500, position: "top-right" });
     emit('saved');
     emit('close');
  } catch (e: any) {
    // Check for permission error
    const isPermissionError = e.response?.status === 401 || (e.response?.data?.message && e.response.data.message.includes("Non hai i permessi"));

    if (isPermissionError) {
        // Show toast - extract full message from backend
        let msg = "Non hai i permessi";
        const responseData = e.response?.data;
        if (typeof responseData === 'string') {
            msg = responseData;
        } else if (responseData?.message) {
            msg = responseData.message;
        } else if (responseData?.details) {
            msg = responseData.details;
        } else if (responseData?.error) {
            msg = responseData.error;
        }
        
        toast.error(msg, { position: "top-right", duration: 4000 });

        // Do NOT close the config modal yet, otherwise this component unmounts 
        // and PermissionRequestModal (which is a child) won't show.
        // We delay the permission modal to read the toast.
        setTimeout(() => {
            showPermissionModal.value = true;
        }, 2500);
        return;
    }

    // Extract error message
    const msg = e.response?.data?.message || e.response?.data?.details || e.message || "Error saving settings";
    const finalMsg = typeof msg === 'object' ? JSON.stringify(msg) : msg;

    // Close modal immediately
    emit('close');

    // Show toast with delay
    setTimeout(() => {
        toast.error(finalMsg, { duration: 4000, position: "top-right" }); // Slightly longer duration for readabilty
    }, 300);
  } finally {
    saving.value = false;
  }
}

function onPermissionModalClose() {
    showPermissionModal.value = false;
    emit('close'); // Close the config modal too when permission flow ends
}
</script>

<style scoped>
.alarm-modal {
    background: #0d0d0d !important; /* Dark theme */
    color: #e6e6e6 !important;
    width: 400px;
    max-width: 95%;
    max-height: 90vh;
    padding: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    border: 1px solid #1d3a3a;
}

.modal-hdr {
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #1d3a3a;
}

.modal-hdr h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: #e6e6e6;
}

.ico-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    color: #e6e6e6;
    transition: color 0.15s;
}

.ico-btn:hover {
    color: #00b3b3;
}

.modal-body {
    padding: 20px;
    flex: 1;
    overflow-y: auto;
}

.subtitle {
    font-size: 0.85rem;
    color: #9ca3af;
    margin-bottom: 24px;
    line-height: 1.4;
}

.section-block {
    margin-bottom: 32px;
}

.sec-title {
    font-size: 0.9rem;
    color: #8b95a1;
    font-weight: 500;
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.control-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}

.control-label {
    font-size: 1rem;
    font-weight: 500;
    color: #d9e2ee;
    flex: 1;
    padding-right: 12px;
}

.control-label.bold {
    font-weight: 600;
}

.mt-4 { margin-top: 1.5rem; }

/* Toggle Switch */
.toggle-switch {
    width: 60px;
    height: 32px;
    border-radius: 999px;
    background-color: #d1d5db; /* gray-300 - darker inactive */
    border: 2px solid #9ca3af; /* gray-400 border for definition */
    position: relative;
    cursor: pointer;
    transition: all 0.25s ease;
    flex-shrink: 0;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
    overflow: hidden; /* Prevent knob from escaping */
    display: inline-block;
}

.toggle-switch:hover {
    border-color: #6b7280; /* darker on hover */
}

.toggle-switch.active {
    background-color: #22c55e; /* green-500 - more vibrant */
    border-color: #16a34a; /* green-600 border */
    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2), inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.knob {
    width: 26px;
    height: 26px;
    background-color: white;
    border-radius: 50%;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 3px;
    transition: all 0.25s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.1);
}

.toggle-switch.active .knob {
    left: calc(100% - 29px); /* 26px knob + 3px padding */
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25), 0 1px 3px rgba(0, 0, 0, 0.15);
}

/* Slider Wrapper */
.slider-wrapper {
    margin-top: 8px;
    padding: 0 8px;
}

/* Row Right with Arrow */
.row-right {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #8b95a1;
}

.chevron {
    color: #6b7280;
}

.value-text {
    font-size: 0.9rem;
}

.text-truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 120px;
}

/* Scrollbar styling for modal body */
.scrollable::-webkit-scrollbar {
    width: 6px;
}
.scrollable::-webkit-scrollbar-track {
    background: transparent;
}
.scrollable::-webkit-scrollbar-thumb {
    background-color: #d1d5db;
    border-radius: 20px;
}

/* Clickable rows */
.clickable {
    cursor: pointer;
    transition: background-color 0.15s;
}

.clickable:hover {
    background-color: rgba(0, 179, 179, 0.05);
}

.chevron {
    transition: transform 0.25s ease;
}

.chevron.rotated {
    transform: rotate(90deg);
}

/* Expandable sections */
.expanded-section {
    margin-top: 12px;
    padding: 12px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    border: 1px solid #2a2f34;
}

/* Expand transition */
.expand-enter-active, .expand-leave-active {
    transition: all 0.3s ease;
    max-height: 500px;
    overflow: hidden;
}

.expand-enter-from, .expand-leave-to {
    max-height: 0;
    opacity: 0;
    margin-top: 0;
    padding-top: 0;
    padding-bottom: 0;
}

/* Notification options */
.notification-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.notification-option {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 8px;
    border-radius: 6px;
    transition: background-color 0.15s;
}

.notification-option:hover {
    background-color: rgba(0, 179, 179, 0.1);
}

.notification-option input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #00b3b3;
}

.option-label {
    font-size: 0.95rem;
    color: #d9e2ee;
    user-select: none;
}

/* Alarm log */
.alarm-log-list {
    max-height: 300px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.no-logs {
    text-align: center;
    color: #8b95a1;
    padding: 20px;
    font-style: italic;
}

.log-item {
    display: flex;
    gap: 12px;
    padding: 10px;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 6px;
    border-left: 3px solid #00b3b3;
}

.log-type {
    flex-shrink: 0;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 700;
    text-align: center;
    min-width: 50px;
}

.log-type.temp {
    background: rgba(239, 68, 68, 0.2);
    color: #f87171;
    border: 1px solid #dc2626;
}

.log-type.hum {
    background: rgba(59, 130, 246, 0.2);
    color: #60a5fa;
    border: 1px solid #3b82f6;
}

.log-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.log-message {
    font-size: 0.9rem;
    color: #e6e6e6;
}

.log-time {
    font-size: 0.75rem;
    color: #8b95a1;
}

.alarm-log-list::-webkit-scrollbar {
    width: 4px;
}

.alarm-log-list::-webkit-scrollbar-track {
    background: transparent;
}

.alarm-log-list::-webkit-scrollbar-thumb {
    background-color: #4a5568;
    border-radius: 20px;
}
</style>
